// v2 monument — a stepped ziggurat: three battered stone terraces filling the 3x3
// plot, a ceremonial stair ramp up the middle of every side (no door — it
// addresses all directions), and corner drums on the first terrace. The summit
// carries an evolving effigy keyed to a donation tier; the model library has no
// tier slot, so this renders the default tier-0 look the flattened file bakes (a
// bare altar with an offering bowl). Footprint-native 3x3. Ported 1:1 from
// age-of-mcp's v2/monument.ts, using the library's shared `foundationSlab` and
// `scaffold` helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, type Part } from "../../lib";

const STONE    = "#8a8f96";
const STONE_DK = "#6c7177";
const WOOD     = "#9c6b3f";
const WOOD_DK  = "#6e4a2a";
const METAL    = "#9fb3c2";
const GOLD     = "#e8c25a";
const GEM      = "#69e0d0";
const TAU      = Math.PI * 2;

const BASE = 0.14;
// The three terraces of the ziggurat.
const T1_TOP = BASE + 0.34;        // 0.48
const T2_TOP = T1_TOP + 0.30;      // 0.78
const TOP    = T2_TOP + 0.26;      // 1.04 — the summit platform

function terrace1(): Part {
  return { geometry: new THREE.BoxGeometry(2.2, 0.34, 2.2), color: STONE,
    position: [0, BASE + 0.17, 0] };
}
function terrace2(): Part {
  return { geometry: new THREE.BoxGeometry(1.6, 0.30, 1.6), color: STONE_DK,
    position: [0, T1_TOP + 0.15, 0] };
}
function terrace3(): Part {
  return { geometry: new THREE.BoxGeometry(1.04, 0.26, 1.04), color: STONE,
    position: [0, T2_TOP + 0.13, 0] };
}

// A ceremonial stair ramp up the middle of each side, foundation -> terrace 1.
// +theta about X drops a ramp's +z end; +/-theta about Z drops its +/-x end.
function stairs(): Part[] {
  const a = 0.757;   // atan2(0.34 rise, 0.36 run)
  const m = 1.24, y = (BASE + T1_TOP) / 2;
  return [
    { geometry: new THREE.BoxGeometry(0.4, 0.05, 0.52), color: STONE_DK,
      position: [0, y, m], rotation: [a, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.4, 0.05, 0.52), color: STONE_DK,
      position: [0, y, -m], rotation: [-a, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.52, 0.05, 0.4), color: STONE_DK,
      position: [m, y, 0], rotation: [0, 0, -a] },
    { geometry: new THREE.BoxGeometry(0.52, 0.05, 0.4), color: STONE_DK,
      position: [-m, y, 0], rotation: [0, 0, a] },
  ];
}

// Corner drums on the first terrace; from tier 3 they carry bright finials.
function drums(tier: number): Part[] {
  const out: Part[] = [];
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      out.push({ geometry: new THREE.CylinderGeometry(0.1, 0.11, 0.2, 6), color: STONE_DK,
        position: [sx * 0.92, T1_TOP + 0.1, sz * 0.92] });
      if (tier >= 3) {
        out.push({ geometry: new THREE.SphereGeometry(0.05, 8, 6),
          color: tier >= 4 ? GOLD : METAL,
          position: [sx * 0.92, T1_TOP + 0.25, sz * 0.92] });
      }
    }
  }
  return out;
}

// The whole plinth: foundation, terraces, stairs and drums.
function ziggurat(tier: number): Part[] {
  return [foundationSlab(2.9), terrace1(), terrace2(), terrace3(),
    ...stairs(), ...drums(tier)];
}

// A standing guardian figure on the summit: robe, torso, head — sized by `k`,
// in `color`. The base of the figure language for tiers 2..5.
function guardian(k: number, color: string): Part[] {
  return [
    { geometry: new THREE.ConeGeometry(0.24 * k, 0.62 * k, 6), color,
      position: [0, TOP + 0.12 + 0.31 * k, 0] },
    { geometry: new THREE.BoxGeometry(0.26 * k, 0.28 * k, 0.18 * k), color,
      position: [0, TOP + 0.12 + 0.74 * k, 0] },
    { geometry: new THREE.SphereGeometry(0.11 * k, 8, 6), color,
      position: [0, TOP + 0.12 + 0.98 * k, 0] },
  ];
}

// The summit effigy for the donation tier.
function crown(tier: number): Part[] {
  if (tier <= 0) {
    // A bare altar awaiting offerings.
    return [
      { geometry: new THREE.BoxGeometry(0.5, 0.16, 0.5), color: STONE_DK,
        position: [0, TOP + 0.08, 0] },
      { geometry: new THREE.CylinderGeometry(0.1, 0.12, 0.05, 6), color: STONE,
        position: [0, TOP + 0.185, 0] },
    ];
  }
  if (tier === 1) {
    // The carved wooden totem.
    return [
      { geometry: new THREE.BoxGeometry(0.4, 0.1, 0.4), color: WOOD_DK,
        position: [0, TOP + 0.05, 0] },
      { geometry: new THREE.CylinderGeometry(0.11, 0.15, 0.52, 6), color: WOOD,
        position: [0, TOP + 0.36, 0] },
      { geometry: new THREE.CylinderGeometry(0.17, 0.17, 0.05, 6), color: WOOD_DK,
        position: [0, TOP + 0.32, 0] },
      { geometry: new THREE.BoxGeometry(0.34, 0.06, 0.06), color: WOOD,
        position: [-0.18, TOP + 0.52, 0], rotation: [0, 0, 0.45] },
      { geometry: new THREE.BoxGeometry(0.34, 0.06, 0.06), color: WOOD,
        position: [0.18, TOP + 0.52, 0], rotation: [0, 0, -0.45] },
      { geometry: new THREE.BoxGeometry(0.16, 0.2, 0.14), color: WOOD,
        position: [0, TOP + 0.74, 0] },
    ];
  }
  if (tier === 2) {
    // The robed stone guardian leaning on a staff.
    return [
      { geometry: new THREE.BoxGeometry(0.44, 0.12, 0.44), color: STONE_DK,
        position: [0, TOP + 0.06, 0] },
      ...guardian(1, STONE),
      { geometry: new THREE.CylinderGeometry(0.025, 0.025, 0.72, 5), color: STONE_DK,
        position: [0.22, TOP + 0.78, 0] },
    ];
  }
  if (tier === 3) {
    // The polished metal guardian raising a sword.
    return [
      { geometry: new THREE.BoxGeometry(0.44, 0.12, 0.44), color: STONE_DK,
        position: [0, TOP + 0.06, 0] },
      ...guardian(1.12, METAL),
      { geometry: new THREE.BoxGeometry(0.05, 0.6, 0.025), color: METAL,
        position: [0.26, TOP + 1.0, 0] },
      { geometry: new THREE.BoxGeometry(0.14, 0.04, 0.05), color: STONE_DK,
        position: [0.26, TOP + 0.72, 0] },
    ];
  }
  // Tiers 4 and 5: the golden colossus lifting a sun disc, arms raised.
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(0.5, 0.14, 0.5), color: GOLD,
      position: [0, TOP + 0.07, 0] },
    ...guardian(1.32, GOLD),
    { geometry: new THREE.CylinderGeometry(0.045, 0.045, 0.5, 5), color: GOLD,
      position: [-0.24, TOP + 1.42, 0], rotation: [0, 0, 0.5] },
    { geometry: new THREE.CylinderGeometry(0.045, 0.045, 0.5, 5), color: GOLD,
      position: [0.24, TOP + 1.42, 0], rotation: [0, 0, -0.5] },
    // The sun disc held aloft (an upright disc facing along z).
    { geometry: new THREE.CylinderGeometry(0.26, 0.26, 0.04, 8), color: GOLD,
      position: [0, TOP + 1.78, 0], rotation: [Math.PI / 2, 0, 0] },
  ];
  // Gold banding along the first terrace's top edges.
  for (const s of [-1, 1]) {
    parts.push(
      { geometry: new THREE.BoxGeometry(2.24, 0.05, 0.07), color: GOLD,
        position: [0, T1_TOP, s * 1.07] },
      { geometry: new THREE.BoxGeometry(0.07, 0.05, 2.24), color: GOLD,
        position: [s * 1.07, T1_TOP, 0] },
    );
  }
  if (tier >= 5) {
    // The jewelled wonder: the crowning gem, gem-tipped obelisks and inlays.
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.17, 0), color: GEM,
      position: [0, TOP + 2.12, 0] });
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        parts.push(
          { geometry: new THREE.BoxGeometry(0.12, 0.46, 0.12), color: STONE,
            position: [sx * 0.66, T2_TOP + 0.23, sz * 0.66] },
          { geometry: new THREE.IcosahedronGeometry(0.07, 0), color: GEM,
            position: [sx * 0.66, T2_TOP + 0.52, sz * 0.66] },
        );
      }
    }
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * TAU;
      parts.push({ geometry: new THREE.IcosahedronGeometry(0.06, 0), color: GEM,
        position: [Math.cos(a) * 0.62, TOP + 0.04, Math.sin(a) * 0.62] });
    }
  }
  return parts;
}

function stage(s: number, tier = 0): Part[] {
  if (s <= 0) return [foundationSlab(2.9), terrace1()];
  if (s === 1) return [foundationSlab(2.9), terrace1(), terrace2(), ...scaffold(2.2, 0.85)];
  if (s === 2) {
    return [foundationSlab(2.9), terrace1(), terrace2(), terrace3(),
      ...drums(0), ...scaffold(1.5, 1.3)];
  }
  // Finished — the ziggurat crowned by the current material tier's effigy.
  return [...ziggurat(tier), ...crown(tier)];
}

export const monumentV2 = defineModel({
  id: "monument-v2",
  tiles: { w: 3, h: 3 },
  batchId: "fable",
  name: "Monument (v2)",
  tags: ["building","monument","low-poly","age-of-mcp","fable"],
  summary: "Low-poly monument.",
  description: "A colossal landmark built as a plain plinth and enriched with refined goods — a grand prestige piece.",
  author: "age-of-mcp",
  footprint: 1.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
});
